import { Config } from "./config";
import {
  ApprovalStatus,
  EmployeeStatus,
  ProjectStatus,
  UserRole,
} from "./enums";

// Enum helpers
export function getApprovalStatusLabel(status: ApprovalStatus): string {
  const labels: Record<ApprovalStatus, string> = {
    [ApprovalStatus.PENDING]: "Pending",
    [ApprovalStatus.APPROVED]: "Approved",
    [ApprovalStatus.REJECTED]: "Rejected",
  };
  return labels[status];
}

export function getUserRoleLabel(role: UserRole): string {
  const labels: Record<UserRole, string> = {
    [UserRole.SUPERADMIN]: "Super Admin",
    [UserRole.MANAGER]: "Manager",
    [UserRole.SUPERVISOR]: "Supervisor",
  };
  return labels[role];
}

export function getEmployeeStatusLabel(status: EmployeeStatus): string {
  const labels: Record<EmployeeStatus, string> = {
    [EmployeeStatus.ACTIVE]: "Active",
    [EmployeeStatus.INACTIVE]: "Inactive",
    [EmployeeStatus.ARCHIVED]: "Archived",
  };
  return labels[status];
}

export function getProjectStatusLabel(status: ProjectStatus): string {
  const labels: Record<ProjectStatus, string> = {
    [ProjectStatus.ACTIVE]: "Active",
    [ProjectStatus.COMPLETED]: "Completed",
    [ProjectStatus.ON_HOLD]: "On Hold",
    [ProjectStatus.CANCELLED]: "Cancelled",
  };
  return labels[status];
}

// Date utilities
export function formatDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatDateTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Currency utilities
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat(Config.i18n.locale, {
    style: "currency",
    currency: Config.i18n.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// Table utilities
export interface TableConfig {
  pageSize: number;
  pageSizeOptions: number[];
  defaultSort?: {
    id: string;
    desc: boolean;
  };
}

export const DEFAULT_TABLE_CONFIG: TableConfig = {
  pageSize: 10,
  pageSizeOptions: [5, 10, 20, 50, 100],
  defaultSort: {
    id: "createdAt",
    desc: true,
  },
};

export function getNestedValue(obj: any, path: string): any {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
}

export function setNestedValue(obj: any, path: string, value: any): any {
  const keys = path.split(".");
  const lastKey = keys.pop()!;
  const target = keys.reduce((current, key) => {
    if (!current[key] || typeof current[key] !== "object") {
      current[key] = {};
    }
    return current[key];
  }, obj);
  target[lastKey] = value;
  return obj;
}

export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as T;
  }

  if (obj instanceof Array) {
    return obj.map((item) => deepClone(item)) as T;
  }

  if (typeof obj === "object") {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }

  return obj;
}

// Filter utilities
export interface FilterOption {
  key: string;
  label: string;
  type: "text" | "select" | "boolean" | "range";
  rangeType?: "date" | "number";
  options?: { value: string; label: string }[];
}

export interface RangeValue {
  min?: string;
  max?: string;
}

export interface FilterSettings {
  searchTerm: string;
  filters: Record<string, string | RangeValue>;
}

export function createFilterOptions<T extends Record<string, any>>(
  data: T[],
  config: {
    textFields?: (keyof T)[];
    selectFields?: {
      key: keyof T;
      label: string;
      options?: { value: string; label: string }[];
    }[];
    rangeFields?: { key: keyof T; label: string; type: "date" | "number" }[];
    booleanFields?: { key: keyof T; label: string }[];
  }
): FilterOption[] {
  const options: FilterOption[] = [];

  // Add text filters
  if (config.textFields) {
    config.textFields.forEach((field) => {
      options.push({
        key: String(field),
        label: String(field)
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase()),
        type: "text",
      });
    });
  }

  // Add select filters
  if (config.selectFields) {
    config.selectFields.forEach((field) => {
      options.push({
        key: String(field.key),
        label: field.label,
        type: "select",
        options: field.options || [],
      });
    });
  }

  // Add range filters
  if (config.rangeFields) {
    config.rangeFields.forEach((field) => {
      options.push({
        key: String(field.key),
        label: field.label,
        type: "range",
        rangeType: field.type,
      });
    });
  }

  // Add boolean filters
  if (config.booleanFields) {
    config.booleanFields.forEach((field) => {
      options.push({
        key: String(field.key),
        label: field.label,
        type: "boolean",
      });
    });
  }

  return options;
}
